export const adminBasePath = "/admin";
export const authBasePath = "/auth";
export const ROUTES = {
    AUTH: {
        login: {
            key: authBasePath + "/",
        },
        register: {
            key: authBasePath + "/register",
        },
        register_with_otp: {
            key: authBasePath + "/register-with-otp",
        },
        two_factor_verification: {
            key: authBasePath + "/2fa",
        },
        forgot_password: {
            key: authBasePath + "/forgot-password",
        },
        reset_password: {
            key: authBasePath + "/reset-password",
            parse: (token: string) => authBasePath + `/reset-password`,
        },
    },
    PRODUCT: {
        product: {
            key: "/store/:store_id/product/:product_id/:menu_type/:user_token",
            parse: (
                store: string,
                product: string,
                menu: string,
                token: string
            ) => `/store/${store}/product/${product}/${menu}/${token}`,
        },
        cart: {
            key: "/store/:store_id/cart/:menu_type/:user_token",
        },
        checkout: {
            key: "/checkout/:service/store/:store_id/:menu_type/:user_token",
        },
        thankyou: {
            key: "/checkout/:service/store/:store_id/:menu_type/:user_token/thank-you",
            parse: (
                store: string,
                menu: string,
                token: string,
                service: string
            ) =>
                `/checkout/${service}/store/${store}/${menu}/${token}/thank-you`,
        },
        profile: {
            key: "/checkout/profile",
        },
        game_stats: {
            key: "/game/results/:event_id/:token",
        },
        tracking_preferences: {
            key: "/tracking-preferences",
        },
    },
    ADMIN: {
        dashboard: {
            key: adminBasePath + "/overview",
        },
        organizations: {
            key: adminBasePath + "/organizations",
        },
        pm_dashboard: {
            key: adminBasePath + "/pm-dashboard",
        },
        message_center: {
            key: adminBasePath + "/message-center",
        },
        reports: {
            key: adminBasePath + "/reports",
        },
        reports_settings: {
            key: adminBasePath + "/reports/settings",
        },
        organization_management: {
            key: "/organization-management",
        },
        organizations_view: {
            key: adminBasePath + "/organizations/:id",
            parse: (id: number) => adminBasePath + `/organizations/${id}`,
        },
        organizations_features: {
            key: adminBasePath + "/organizations/:id/features",
            parse: (id: number) =>
                adminBasePath + `/organizations/${id}/features`,
        },
        organizations_firebase_settings: {
            key: adminBasePath + "/organizations/:id/firebase",
            parse: (id: number) =>
                adminBasePath + `/organizations/${id}/firebase`,
        },
        organizations_mobile_app_accounts: {
            key: adminBasePath + "/organizations/:id/mobile-app-accounts",
            parse: (id: number) =>
                adminBasePath + `/organizations/${id}/mobile-app-accounts`,
        },
        organizations_default_content_generator: {
            key: adminBasePath + "/organizations/:id/default-content-generator",
            parse: (id: number) =>
                adminBasePath + `/organizations/${id}/default-content-generator`,
        },
        permissions: {
            key: adminBasePath + "/permissions",
        },
        roles: {
            key: adminBasePath + "/roles",
        },
        feature_bundles: {
            key: adminBasePath + "/feature-bundles",
        },
        integration_templates: {
            key: adminBasePath + "/integration-templates",
        },
        integration_templates_create: {
            key: adminBasePath + "/integration-templates/create",
        },
        integration_templates_view: {
            key: adminBasePath + "/integration-templates/:id",
            parse: (id: number) =>
                adminBasePath + `/integration-templates/${id}`,
        },
        analytics: {
            key: adminBasePath + "/analytics",
        },
        analytics_tenant: {
            key: adminBasePath + "/analytics/tenant/:id",
            parse: (id: string) => adminBasePath + `/analytics/tenant/${id}`,
        },
        // The event name travels as the `event` query param, never a path segment
        analytics_events: {
            key: adminBasePath + "/analytics/events",
        },
        analytics_tenant_events: {
            key: adminBasePath + "/analytics/tenant/:id/events",
            parse: (id: string) =>
                adminBasePath + `/analytics/tenant/${id}/events`,
        },
        users: {
            key: adminBasePath + "/users",
        },
        users_view: {
            key: adminBasePath + "/users/:id",
            parse: (id: number) => adminBasePath + `/users/${id}`,
        },
        portal_users: {
            key: adminBasePath + "/portal-users",
        },
        profile: {
            key: adminBasePath + "/profile",
        },
        settings: {
            key: adminBasePath + "/settings",
        },
        logs: {
            key: adminBasePath + "/logs",
        },
        product_sync_logs: {
            key: adminBasePath + "/logs/product-sync",
        },
        promotion_sync_logs: {
            key: adminBasePath + "/logs/promotion-sync",
        },
        reward_sync_logs: {
            key: adminBasePath + "/logs/reward-sync",
        },
        notification_logs: {
            key: adminBasePath + "/logs/notification",
        },
        registration_and_verifications_logs: {
            key: adminBasePath + "/logs/registration-and-verification",
        },
        ai_playground: {
            key: adminBasePath + "/ai-playground",
        },
        image_generation: {
            key: adminBasePath + "/image-generation",
        },
        image_generation_sessions: {
            key: adminBasePath + "/image-generation/sessions",
        },
        image_prompt_versions: {
            key: adminBasePath + "/image-generation/prompt-versions",
        },
        redis_cache: {
            key: adminBasePath + "/redis",
        },
        brands: {
            key: adminBasePath + "/brands",
        },
        app_store_monitor: {
            key: adminBasePath + "/app-monitor",
        },
        announcements: {
            key: adminBasePath + "/announcements",
        },
        announcements_create: {
            key: adminBasePath + "/announcements/create",
        },
        announcements_edit: {
            key: adminBasePath + "/announcements/:id/edit",
            parse: (id: number) => adminBasePath + `/announcements/${id}/edit`,
        },
        announcements_metrics: {
            key: adminBasePath + "/announcements/:id/metrics",
            parse: (id: number) =>
                adminBasePath + `/announcements/${id}/metrics`,
        },
        resourceLinks: { key: adminBasePath + "/resource-links" },
        documentationLinks: {
            key: adminBasePath + "/documentation-links",
        },
    },
    USER: {
        brand_tags: {
            key: "/brand-tags",
        },
        brand_tags_edit: {
            key: "/brand-tags/:id",
            parse: (id: number) => `/brand-tags/${id}`,
        },
        brand_tags_create: {
            key: "/brand-tags/create",
        },
        overview: {
            key: "/overview",
        },
        myApp: {
            key: "/overview",
            path: "/overview",
        },
        organization_management: {
            key: "/organization-management",
        },
        notifications: {
            key: "/notifications",
        },
        notifications_create: {
            key: "/notifications/create",
        },
        notifications_edit: {
            key: "/notifications/:id",
            parse: (id: number) => `/notifications/${id}`,
        },
        stores: {
            key: "/stores",
        },
        stores_edit: {
            key: "/stores/:id",
            parse: (id: number, params?: string) =>
                `/stores/${id}${params ?? ""}`,
        },
        achievements: {
            key: "/achievements",
        },
        achievements_create: {
            key: "/achievements/create",
        },
        single_achievements_edit: {
            key: "/single-achievements/:id",
            parse: (id: number) => `/single-achievements/${id}`,
        },
        single_achievements_create: {
            key: "/single-achievements/create",
        },
        tiered_achievements_edit: {
            key: "/tiered-achievements/:id",
            parse: (id: number) => `/tiered-achievements/${id}`,
        },
        tiered_achievements_create: {
            key: "/tiered-achievements/create",
        },
        categories: {
            key: "/categories",
        },
        categories_edit: {
            key: "/categories/:id",
            parse: (id: number) => `/categories/${id}`,
        },
        categories_create: {
            key: "/categories/create",
        },
        brand_products: {
            key: "/brand-products",
        },
        brand_products_edit: {
            key: "/brand-products/:id",
            parse: (id: number) => `/brand-products/${id}`,
        },
        brand_products_create: {
            key: "/brand-products/create",
        },
        brand_homepage: {
            key: "/brand-homepage",
        },
        brand_stores: {
            key: "/brand-stores",
        },
        brand_stores_create: {
            key: "/brand-stores/create",
        },
        brand_stores_edit: {
            key: "/brand-stores/:id",
            parse: (id: number) => `/brand-stores/${id}`,
        },
        promotions: {
            key: "/promotions",
        },
        promotions_edit: {
            key: "/promotions/:id",
            parse: (id: number) => `/promotions/${id}`,
        },
        promotions_create: {
            key: "/promotions/create",
        },
        punchcards: {
            key: "/punchcards",
        },
        punchcards_create: {
            key: "/punchcards/create",
        },
        punchcards_edit: {
            key: "/punchcards/:id",
            parse: (id: number) => `/punchcards/${id}`,
        },
        punchcards_dashboard: {
            key: "/punchcards/:id/dashboard",
            parse: (id: number) => `/punchcards/${id}/dashboard`,
        },
        coupons: {
            key: "/coupons",
        },
        coupons_create: {
            key: "/coupons/create",
        },
        coupons_edit: {
            key: "/coupons/:id",
            parse: (id: number) => `/coupons/${id}`,
        },
        integrations: {
            key: "/integrations",
        },
        integrations_create: {
            key: "/integrations/create",
        },
        sandbox_tools: {
            key: "/sandbox-tools",
        },
        aiq_flow_events: {
            key: "/sandbox-tools/aiq-flow-events",
        },
        aiq_flow_events_create: {
            key: "/sandbox-tools/aiq-flow-events/create",
        },
        integrations_edit: {
            key: "/integrations/:id",
            parse: (id: number) => `/integrations/${id}`,
        },
        geofences: {
            key: "/geofences",
        },
        geofences_create: {
            key: "/geofences/create",
        },
        geofences_edit: {
            key: "/geofences/:id",
            parse: (id: number) => `/geofences/${id}`,
        },
        customers: {
            key: "/customers",
        },
        customers_profile: {
            key: "/customers/:id",
            parse: (id: number) => `/customers/${id}`,
        },
        rewards: {
            key: "/rewards",
        },
        rewards_create: {
            key: "/rewards/create",
        },
        rewards_edit: {
            key: "/rewards/:id",
            parse: (id: number) => `/rewards/${id}`,
        },
        reward_categories: {
            key: "/reward-categories",
        },
        gift_card_settings: {
            key: "/gift-card-settings",
        },
        settings: {
            key: "/settings",
        },
        delivery_settings: {
            key: "/settings/delivery",
        },
        custom_content: {
            key: "/custom-content",
        },
        custom_content_haptics: {
            key: "/custom-content/haptics",
        },
        reports: {
            key: "/reports",
        },
        brand: {
            key: "/brand",
        },
        referral_report: {
            key: "/reports/referrals",
        },
        user_registration_report: {
            key: "/reports/user-registrations",
        },
        achievements_earned_report: {
            key: "/reports/achievements-earned",
        },
        orders: {
            key: "/orders",
        },
        games: {
            key: "/games",
        },
        games_create: {
            key: "/games/create",
        },
        games_edit: {
            key: "/games/:id",
            parse: (id: number) => `/games/${id}`,
        },
        game_events: {
            key: "/game-events",
        },
        game_events_edit: {
            key: "/game-events/:id",
            parse: (id: number) => `/game-events/${id}`,
        },
        game_events_create: {
            key: "/game-events/create",
        },
        games_dashboard: {
            key: "/games/:id/dashboard",
            parse: (id: number) => `/games/${id}/dashboard`,
        },
        game_entries: {
            key: "/game-entries",
        },
        game_entries_create: {
            key: "/game-entries/create",
        },
        daily_rewards: {
            key: "/daily-rewards",
        },
        daily_rewards_edit: {
            key: "/daily-rewards/:id",
            parse: (id: number) => `/daily-rewards/${id}`,
        },
        daily_rewards_create: {
            key: "/daily-rewards/create",
        },
        gamification: {
            key: "/gamification",
        },
        micro_currencies: {
            key: "/micro-currencies",
        },
        micro_currencies_create: {
            key: "/micro-currencies/create",
        },
        micro_currencies_edit: {
            key: "/micro-currencies/:id",
            parse: (id: number) => `/micro-currencies/${id}`,
        },
        app_feedbacks: {
            key: "/app-feedbacks",
        },
        analytics: {
            key: "/analytics",
            parse: (projectId: string) =>
                `https://mixpanel.com/project/${projectId}/`,
        },
        download_links: {
            key: "/download-links",
        },
        download_links_create: {
            key: "/download-links/create",
        },
        download_links_edit: {
            key: "/download-links/:id",
            parse: (id: number) => `/download-links/${id}`,
        },
        tiers: {
            key: "/tiers",
        },
        tiers_edit: {
            key: "/tiers/:id",
            parse: (id: number) => `/tiers/${id}`,
        },
        tiers_create: {
            key: "/tiers/create",
        },
        sweepstakes: {
            key: "/sweepstakes",
        },
        sweepstakes_create: {
            key: "/sweepstakes/create",
        },
        sweepstakes_edit: {
            key: "/sweepstakes/:id",
            parse: (id: number) => `/sweepstakes/${id}`,
        },
        sweepstakes_dashboard: {
            key: "/sweepstakes/:id/dashboard",
            parse: (id: number) => `/sweepstakes/${id}/dashboard`,
        },
        qr_codes: {
            key: "/qr-codes",
        },
        in_app_qr_codes: {
            key: "/in-app-qr-codes",
        },
        in_app_qr_codes_create: {
            key: "/in-app-qr-codes/create",
        },
        in_app_qr_codes_edit: {
            key: "/in-app-qr-codes/:id",
            parse: (id: number) => `/in-app-qr-codes/${id}`,
        },
        brand_boosts: {
            key: "/brand-boosts",
        },
        brand_boosts_create: {
            key: "/brand-boosts/create",
        },
        brand_boosts_edit: {
            key: "/brand-boosts/:id",
            parse: (id: number) => `/brand-boosts/${id}`,
        },
        tags: {
            key: "/tags",
        },
        tags_create: {
            key: "/tags/create",
        },
        tags_edit: {
            key: "/tags/:id",
            parse: (id: number) => `/tags/${id}`,
        },
        engagement: {
            key: "/engagement",
        },
        engagement_create: {
            key: "/engagement/create",
        },
        engagement_detail: {
            key: "/engagement/:id",
            parse: (id: number) => `/engagement/${id}`,
        },
        engagement_edit: {
            key: "/engagement/:id/edit",
            parse: (id: number) => `/engagement/${id}/edit`,
        },
    },
};
